import { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import Prompt from "@/models/prompt/prompt";
import usePrompts from "@/hooks/usePrompts";
import { Option } from "../components/prompt-library/PromptLibraryListView";
import { ColumnFilter } from "@tanstack/react-table";

interface UsePromptLibraryReturn {
  userPrompts: Prompt[];
  search: string;
  promptsLoading: boolean;
  loadNextPage: () => void;
  handleSearchChange: (searchValue: string) => void;
  handleSorting: (option: Option) => void;
  handleFiltering: (option: Option | undefined) => void;
  setUserPrompts: (prompts: Prompt[]) => void;
  hasNextPage: boolean;
}

const usePromptPagination = (): UsePromptLibraryReturn => {
  // Hooks
  const { useQueryMyPrompts } = usePrompts();

  // Data state
  const [loadedPrompts, setLoadedPrompts] = useState<Prompt[]>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [pagesLoaded, setPagesLoaded] = useState<number>(0);

  // Pagination state
  const [pageIndex, setPageIndex] = useState(0); // zero-based index
  const [pageSize] = useState(10);

  // Sorting state
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortAscending, setSortAscending] = useState<boolean>(false);

  // Filtering state
  const [filters, setFilters] = useState<ColumnFilter[]>([]);

  // Search state
  // Search state
  const [input, setInput] = useState(""); // For capturing immediate user input
  const [search, setSearch] = useState(""); // For the actual debounced search

  // Abort controller state
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  // Fetch data
  const fetchedPrompts = useQueryMyPrompts(
    {
      pageNumber: pageIndex + 1, // Convert zero-based index to one-based page number
      pageSize,
      sortAscending,
      sortBy,
      search,
      filters,
    },
    abortController?.signal
  );

  // Effects
  useEffect(() => {
    if (
      fetchedPrompts.isFetched &&
      fetchedPrompts.data &&
      fetchedPrompts.data.data
    ) {
      if (pageIndex === 0) {
        setLoadedPrompts(fetchedPrompts.data.data);
      } else if (pageIndex + 1 >= pagesLoaded) {
        setLoadedPrompts((prev) => [...prev, ...fetchedPrompts.data.data]);
      }
      setPagesLoaded((prev) => prev + 1);
      setHasNextPage(fetchedPrompts.data.hasMore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedPrompts.data]);

  useEffect(() => {
    const debouncedFetch = debounce((searchValue: string) => {
      if (abortController) {
        abortController.abort();
      }

      const controller = new AbortController();
      setAbortController(controller);

      setPageIndex(0);
      setSearch(searchValue);
    }, 300);

    debouncedFetch(input);

    return () => {
      debouncedFetch.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const loadNextPage = useCallback(() => {
    if (hasNextPage && !fetchedPrompts.isPending) {
      setPageIndex((prev) => prev + 1);
    }
  }, [hasNextPage, fetchedPrompts.isPending]);

  const handleSorting = (option: Option) => {
    resetPagination();
    const splittedString = option.value.split(":");
    setSortBy(splittedString[0]);
    setSortAscending(splittedString[1] === "asc");
  };

  const handleFiltering = (option: Option | undefined) => {
    resetPagination();
    if (!option) {
      setFilters([]);
      return;
    }

    const splittedString = option.value.split(":");
    const filter: ColumnFilter = {
      id: splittedString[0],
      value: splittedString[1],
    };

    setFilters([filter]);
  };

  // Debounce the input change handler
  const debouncedSetInput = useMemo(
    () => debounce((searchValue: string) => setInput(searchValue), 300),
    []
  );

  const handleSearchChange = (searchValue: string) => {
    debouncedSetInput(searchValue);
  };

  const resetPagination = () => {
    setLoadedPrompts([]);
    setPagesLoaded(0);
    setPageIndex(0);
    setHasNextPage(false);
  };

  return {
    userPrompts: loadedPrompts,
    promptsLoading: fetchedPrompts.isPending,
    search,
    loadNextPage,
    handleFiltering,
    handleSorting,
    handleSearchChange,
    setUserPrompts: setLoadedPrompts,
    hasNextPage,
  };
};

export default usePromptPagination;
